/**
 * The Add row's editor: a text box in the Symbol cell, and the suggestions under
 * it.
 *
 * ## Nothing goes in without Enter
 *
 * The editor **never hands AG Grid a value** — `isCancelAfterEnd` answers true
 * on every exit — so Escape, Tab, a click elsewhere and focus leaving the grid
 * all end the edit the same way: the text is dropped and nothing is filed. The
 * only way in is Enter (or picking a suggestion, which is Enter on that row),
 * and that goes to the host's `onAdd`, not into the cell.
 *
 * ## Enter keeps the editor open
 *
 * Filing four symbols is four Enters, not four clicks back into the row. So
 * Enter submits and stays: the box clears on success, or keeps what was refused
 * so it can be fixed. AG Grid's own Enter would end the edit, which is why
 * `addRowSuppressesKey` takes Enter and ↑/↓ away from it while this is open —
 * AG Grid's listener is on the row container and runs before React's.
 *
 * If the user has typed on while an add was in flight, their new text wins: the
 * box is only reset when it still holds what was submitted.
 *
 * ## The top suggestion is highlighted, and Enter adds it
 *
 * The owner's call (2026-09-21): `tsl` ⏎ adds TSLA. The first row of the list is
 * highlighted the moment the list appears, so what Enter will file is on screen
 * before the key is pressed — the safeguard is *seeing* the pick, not refusing
 * it. ↑/↓ move the highlight and stay inside the list; there is no "nothing
 * highlighted" state to arrow into, because the host ranks an exact ticker first
 * (`ms` offers MS above MSFT), so the text as typed is always the top row when
 * it is a ticker at all. With no list — nothing matches, or the index has not
 * landed — Enter submits the text and the host validates it.
 *
 * A paste of several symbols gets no list at all: that is the host's rule
 * (`addRowSuggestions`), because a list somebody pasted is deliberate, and
 * guessing at each entry of it could file symbols nobody chose.
 *
 * ## The list is portalled, `position: fixed`
 *
 * AG Grid positions rows with `transform`, and a fixed element inside a
 * transformed ancestor is fixed to *that* — so a list rendered in the cell would
 * be clipped by the row. It goes to the island root instead, which inherits the
 * `--qf-*` variables the colours read, and flips above the row when there is no
 * room below: this row is the last thing in the grid, and usually near the
 * bottom of the window.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { useGridCellEditor, type CustomCellEditorProps } from "ag-grid-react";

import type { AddRowSuggestion } from "../lib/add-row.js";

export interface AddRowEditorParams {
  /** The host's completions for the box — `AddRowBinding.suggest`. */
  suggest: (text: string, caret: number) => readonly AddRowSuggestion[];
  /**
   * File what was typed. Resolves to what the box should hold next and the
   * message the row now shows; never rejects.
   */
  submit: (text: string) => Promise<{ remaining: string; message: string | null }>;
  /** The text changed, so a message about the previous text is stale. */
  clearMessage: () => void;
  placeholder: () => string;
  /** Where the suggestion list is portalled — the island root. */
  portal: () => HTMLElement | null;
  /**
   * Text pasted onto the row before the box existed, taken once — the paste
   * that opened this editor (`ScreenerGrid`'s `handlePaste`). `null` otherwise.
   */
  takeSeed?: () => string | null;
}

/** Rows in the list. Past this the answer is a better prefix, not a scroll. */
const MAX_SUGGESTIONS = 8;
/** One option's height in px, for deciding whether the list fits below. */
const OPTION_HEIGHT = 26;
/** Wide enough for a ticker and most of a company name, whatever the column. */
const LIST_WIDTH = 300;

interface Placement {
  left: number;
  top?: number;
  bottom?: number;
}

/**
 * The first character AG Grid started the edit with, if it was one.
 *
 * A letter typed at the focused cell starts the edit and is marked handled, so
 * the browser never types it into this box — it arrives as `eventKey` instead.
 * Enter, F2 and a click arrive as a key name or nothing, and start empty.
 */
function seedOf(eventKey: string | null | undefined): string {
  return eventKey && eventKey.length === 1 && eventKey !== " " ? eventKey : "";
}

/** A paste as one line: every run of whitespace, line breaks included, one space. */
function flattenPaste(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

export function AddRowEditor(props: CustomCellEditorProps & AddRowEditorParams) {
  // **The whole of "nothing is ever added without Enter".** Whatever ends the
  // edit, the grid is told to discard the value — the cell has no value to
  // write, and the add has already happened, or not, through `submit`.
  useGridCellEditor({ isCancelAfterEnd: () => true });

  // Once, at mount: `takeSeed` clears what it hands over.
  const [seed] = useState(() => {
    const pasted = props.takeSeed?.();
    return pasted ? flattenPaste(pasted) : seedOf(props.eventKey);
  });
  const [text, setText] = useState(seed);
  const [caret, setCaret] = useState(seed.length);
  /** The highlighted row — the top one whenever the list changes. */
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  /** The last refusal, for the box's own border and tooltip. */
  const [refused, setRefused] = useState<string | null>(null);
  const [place, setPlace] = useState<Placement | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();

  /** What the box holds now, for a submit that resolves after more typing. */
  const held = useRef(text);
  held.current = text;
  const live = useRef(true);
  useEffect(
    () => () => {
      live.current = false;
    },
    [],
  );

  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  // `suggest` is a stable getter from the grid (see `ScreenerGrid`), so the text
  // and the caret are the whole of what the list depends on.
  const suggestions = useMemo(
    () => (text.trim() ? props.suggest(text, caret).slice(0, MAX_SUGGESTIONS) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `suggest` is stable
    [text, caret],
  );

  // A new list starts on its top row — before paint, so the highlight never
  // shows on a row the new list does not have, or on the wrong one.
  useLayoutEffect(() => {
    setActive(0);
  }, [suggestions]);

  useLayoutEffect(() => {
    const el = input.current;
    if (!el || suggestions.length === 0) {
      setPlace(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const needed = suggestions.length * OPTION_HEIGHT + 8;
    const below = window.innerHeight - rect.bottom;
    // Below when it fits, or when above is no better — the mock's home for it.
    setPlace(
      below >= needed || below >= rect.top
        ? { left: rect.left, top: rect.bottom + 2 }
        : { left: rect.left, bottom: window.innerHeight - rect.top + 2 },
    );
  }, [suggestions]);

  const submit = (value: string) => {
    if (busy || !value.trim()) return;
    setBusy(true);
    const submitted = held.current;
    void props.submit(value).then(({ remaining, message }) => {
      if (!live.current) return;
      setBusy(false);
      setRefused(message);
      // Only when nothing was typed since — the next symbol, typed while this
      // one was on the wire, is not ours to overwrite.
      if (held.current === submitted) {
        setText(remaining);
        setCaret(remaining.length);
      }
      input.current?.focus();
    });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((i) => Math.max(0, Math.min(i + 1, suggestions.length - 1)));
        return;
      case "ArrowUp":
        event.preventDefault();
        // Stops at the top row rather than leaving the list: see the header.
        setActive((i) => Math.max(i - 1, 0));
        return;
      case "Enter": {
        event.preventDefault();
        event.stopPropagation();
        const pick = suggestions[active];
        submit(pick ? (pick.value ?? pick.symbol) : text);
        return;
      }
      default:
        return;
    }
  };

  const portal = props.portal() ?? (typeof document !== "undefined" ? document.body : null);

  return (
    <>
      <input
        ref={input}
        value={text}
        role="combobox"
        aria-label="Add a symbol"
        aria-expanded={suggestions.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={suggestions[active] ? `${listId}-${active}` : undefined}
        aria-invalid={refused ? true : undefined}
        aria-busy={busy || undefined}
        title={refused ?? undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder={props.placeholder()}
        onChange={(event) => {
          setText(event.target.value);
          setCaret(event.target.selectionStart ?? event.target.value.length);
          // No reset of the highlight here: a new list resets it, to its top.
          setRefused(null);
          props.clearMessage();
        }}
        // The caret moves without the text changing — a click, an arrow — and
        // the host ranks on the operand it sits in.
        onSelect={(event) => {
          const el = event.target as HTMLInputElement;
          setCaret(el.selectionStart ?? el.value.length);
        }}
        // A column copied from a sheet is one symbol per line, and a one-line
        // box may drop the breaks rather than space them — `AAPL⏎MSFT` would
        // arrive as `AAPLMSFT`. Every run of whitespace becomes one space.
        onPaste={(event) => {
          const pasted = event.clipboardData.getData("text");
          if (!/[\r\n\t]/.test(pasted)) return;
          event.preventDefault();
          const el = event.currentTarget;
          const start = el.selectionStart ?? el.value.length;
          const end = el.selectionEnd ?? start;
          const flat = flattenPaste(pasted);
          const next = el.value.slice(0, start) + flat + el.value.slice(end);
          setText(next);
          setCaret(start + flat.length);
          setRefused(null);
          props.clearMessage();
        }}
        onKeyDown={onKeyDown}
        style={refused ? { ...INPUT, borderColor: "var(--qf-negative)" } : INPUT}
      />
      {place && portal
        ? createPortal(
            <div
              id={listId}
              role="listbox"
              aria-label="Symbols"
              style={{ ...LIST, left: place.left, top: place.top, bottom: place.bottom }}
            >
              {suggestions.map((row, index) => (
                <div
                  key={row.symbol}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  // Focus must stay in the box: a blur here ends the edit before
                  // the click lands, since the list is outside the grid.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => submit(row.value ?? row.symbol)}
                  style={index === active ? { ...OPTION, background: "var(--qf-selection)" } : OPTION}
                >
                  <span style={SYMBOL}>{row.symbol}</span>
                  <span style={NAME}>{row.name ?? ""}</span>
                </div>
              ))}
            </div>,
            portal,
          )
        : null}
    </>
  );
}

const INPUT: CSSProperties = {
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  padding: "0 0.4rem",
  font: "inherit",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  // Longhand, because a refusal swaps `borderColor` alone — React warns, and
  // can drop it, when one render mixes the shorthand with a longhand.
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--qf-accent)",
  borderRadius: 3,
  outline: "none",
};

const LIST: CSSProperties = {
  position: "fixed",
  zIndex: 1000,
  width: LIST_WIDTH,
  padding: "0.2rem 0",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  // The one literal, as in `FieldRef`'s popover: a shadow darkens in every theme.
  boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
  fontSize: "0.78rem",
};

const OPTION: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  height: OPTION_HEIGHT,
  padding: "0 0.6rem",
  cursor: "pointer",
  color: "var(--qf-text)",
};

const SYMBOL: CSSProperties = {
  flex: "0 0 auto",
  minWidth: "4.5rem",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontWeight: 700,
};

const NAME: CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "var(--qf-text-secondary)",
};
